import { isEmbeddedMode, isV2OnlyTopLevelPath } from '../auth/embedMode';
import { ROUTE_PATHS } from '../routes/routePaths';
import { v3ShellOrigin } from './constants';

/**
 * Iframe → V3 shell navigation / new-tab helpers.
 *
 * Single place for: window.open, Ctrl/Cmd+click, middle-click, and
 * right-click "Open link in new tab" so embedded V2 always opens the V3 wrapper.
 */

const FALLBACK_PARENT_ORIGIN = v3ShellOrigin;
const nativeOpen = globalThis.open.bind(globalThis);

function getAncestorOrigin(): string | undefined {
  try {
    const ancestorOrigin = globalThis.location.ancestorOrigins?.item(0);
    return ancestorOrigin ? ancestorOrigin.replace(/\/$/, '') : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Origin of the V3 shell window.
 * Prefer `ancestorOrigins` — it stays the parent after a hard navigation
 * inside this frame. `document.referrer` becomes this app's own origin after
 * that reload (component crashes use `location.replace`), and posting to it
 * throws, so the shell never navigates. When neither live source is a
 * different origin, use the configured shell origin.
 */
function getParentOrigin(): string | undefined {
  const ancestorOrigin = getAncestorOrigin();
  if (ancestorOrigin && ancestorOrigin !== globalThis.location.origin) {
    return ancestorOrigin;
  }

  try {
    if (document.referrer) {
      const referrerOrigin = new URL(document.referrer).origin;
      if (referrerOrigin !== globalThis.location.origin) return referrerOrigin;
    }
  } catch {
    // ignore malformed referrer
  }

  return FALLBACK_PARENT_ORIGIN?.replace(/\/$/, '') || undefined;
}

export type ParentProductHomePayload = {
  productId?: number;
  canonicalUrl?: string;
  catId?: number;
  catType?: number;
};

function toParentProductHomePayload(
  productIdOrPayload?: number | ParentProductHomePayload
): ParentProductHomePayload {
  if (typeof productIdOrPayload === 'object' && productIdOrPayload !== null) {
    return productIdOrPayload;
  }
  return { productId: productIdOrPayload };
}

/**
 * Asks the V3 shell to navigate to the current product's slug-based home page.
 * Does not navigate the iframe — avoids flashing the embedded dashboard.
 *
 * Pass `canonicalUrl` when the embed already resolved `/products/{id}/redirect`
 * so V3 can navigate without a second redirect call. Advanced Search / All Alerts
 * still pass `productId` only.
 *
 * Returns false when not embedded or the parent origin is unknown.
 */
export function requestParentProductHome(
  productIdOrPayload?: number | ParentProductHomePayload
): boolean {
  if (window.parent === window) return false;
  const parentOrigin = getParentOrigin() ?? FALLBACK_PARENT_ORIGIN;
  if (!parentOrigin) return false;

  const { productId, canonicalUrl, catId, catType } =
    toParentProductHomePayload(productIdOrPayload);

  window.parent.postMessage(
    {
      type: 'NAVIGATE_PRODUCT_HOME',
      ...(productId && productId > 0 ? { productId } : {}),
      ...(canonicalUrl ? { canonicalUrl } : {}),
      ...(typeof catId === 'number' ? { catId } : {}),
      ...(typeof catType === 'number' ? { catType } : {}),
    },
    parentOrigin
  );
  return true;
}

/**
 * Embedded: ask the V3 shell to open the current product dashboard.
 * Standalone V2: fall back to `/category-home-page`.
 */
export function goToParentProductHomeOrCategoryHome(productId?: number): void {
  if (requestParentProductHome(productId)) return;
  window.location.assign(ROUTE_PATHS.CATEGORY_HOME);
}

/**
 * Asks the V3 shell to navigate to native Manage Alerts.
 * Does not navigate the iframe — avoids flashing the embedded V2 My Alerts page.
 *
 * Returns false when not embedded or the parent origin is unknown.
 */
export function requestParentManageAlerts(): boolean {
  if (window.parent === window) return false;
  const parentOrigin = getParentOrigin() ?? FALLBACK_PARENT_ORIGIN;
  if (!parentOrigin) return false;

  window.parent.postMessage({ type: 'NAVIGATE_MANAGE_ALERTS' }, parentOrigin);
  return true;
}

/**
 * All Alerts Back without a `fromPage`: asks the V3 shell to open the page the
 * user came from as a new history entry, so browser Back from there still
 * returns to All Alerts (`history.back()` would make All Alerts a Forward entry).
 *
 * Returns false when not embedded or the parent origin is unknown.
 */
export function requestParentAllAlertsBack(): boolean {
  if (window.parent === window) return false;
  const parentOrigin = getParentOrigin() ?? FALLBACK_PARENT_ORIGIN;
  if (!parentOrigin) return false;

  window.parent.postMessage({ type: 'NAVIGATE_ALL_ALERTS_BACK' }, parentOrigin);
  return true;
}

/**
 * Asks the V3 shell to open `path` (a V2 app path) as a new history entry
 * instead of navigating this iframe. Entries the iframe adds belong to that
 * iframe instance, so after a page refresh browser Back could not reach them.
 *
 * Returns false when not embedded or the parent origin is unknown.
 */
export function requestParentNavigate(path: string): boolean {
  if (window.parent === window) return false;
  const parentOrigin = getParentOrigin() ?? FALLBACK_PARENT_ORIGIN;
  if (!parentOrigin) return false;

  window.parent.postMessage({ type: 'NAVIGATE_SHELL', path }, parentOrigin);
  return true;
}

/**
 * Asks the V3 shell to navigate to the app home page (`/dashboard`).
 * Does not navigate the iframe — avoids flashing the embedded V2 category home.
 *
 * Returns false when not embedded or the parent origin is unknown.
 */
export function requestParentDashboard(): boolean {
  if (window.parent === window) return false;
  const parentOrigin = (getParentOrigin() ?? FALLBACK_PARENT_ORIGIN)?.replace(
    /\/$/,
    ''
  );
  if (!parentOrigin) return false;

  try {
    window.parent.postMessage({ type: 'NAVIGATE_DASHBOARD' }, parentOrigin);
    return true;
  } catch {
    // Target origin did not match the parent. Load the shell home directly
    // instead of the embedded category home, which renders nothing while it loads.
    try {
      window.parent.location.assign(`${parentOrigin}/dashboard`);
      return true;
    } catch {
      return false;
    }
  }
}

/**
 * Embedded: ask the V3 shell to open `/dashboard`.
 * Standalone V2: fall back to `/category-home-page`.
 */
export function goToParentDashboardOrCategoryHome(): void {
  if (requestParentDashboard()) return;
  window.location.assign(ROUTE_PATHS.CATEGORY_HOME);
}

/**
 * Reports the theme this iframe just resolved (product/category changed inside
 * V2) so the shell chrome re-colours without waiting for its own URL round-trip.
 */
export function notifyParentThemeChanged(themeId: number): void {
  if (window.parent === window) return;
  const parentOrigin = getParentOrigin() ?? FALLBACK_PARENT_ORIGIN;
  if (!parentOrigin) return;

  window.parent.postMessage({ type: 'THEME_CHANGED', themeId }, parentOrigin);
}

/**
 * Ask the V3 shell to scroll its page back to the top.
 * Compare → report stays inside this iframe, so a scroll the user made on the
 * compare page is kept by the shell when the report replaces the iframe body.
 *
 * V3 should listen for `{ type: 'SCROLL_TO_TOP' }` and scroll the shell
 * content (and the iframe viewport) to the top.
 */
export function requestParentScrollToTop(): void {
  if (window.parent === window) return;
  const parentOrigin = getParentOrigin() ?? FALLBACK_PARENT_ORIGIN;
  if (!parentOrigin) return;

  window.parent.postMessage({ type: 'SCROLL_TO_TOP' }, parentOrigin);
}

/**
 * Embedded modal overlay — ask V3 to cover header + left nav with the same
 * frosted backdrop the iframe uses for Bootstrap modals.
 *
 * V3 must listen for:
 *   { type: 'V2_MODAL_OPEN' }  → show shell chrome overlay (header + left nav)
 *   { type: 'V2_MODAL_CLOSE' } → remove shell chrome overlay
 *
 * Posts only when the active state changes (nested modals stay open).
 */
let shellModalOverlayActive = false;

export function setParentModalOverlayActive(active: boolean): void {
  if (!isEmbeddedMode()) return;
  const parentOrigin = getParentOrigin() ?? FALLBACK_PARENT_ORIGIN;
  if (!parentOrigin) return;
  if (active === shellModalOverlayActive) return;

  shellModalOverlayActive = active;
  window.parent.postMessage(
    { type: active ? 'V2_MODAL_OPEN' : 'V2_MODAL_CLOSE' },
    parentOrigin
  );
}

/** True when any Bootstrap / custom modal backdrop is present in this document. */
export function hasActiveModalBackdrop(): boolean {
  return Boolean(
    document.querySelector(
      '.modal-backdrop, [data-v2-shell-modal-overlay]'
    )
  );
}

/** Sync shell overlay from the current DOM (used by the global observer). */
export function syncParentModalOverlayFromDom(): void {
  setParentModalOverlayActive(hasActiveModalBackdrop());
}

/**
 * After an iframe reload the module-level `shellModalOverlayActive` resets to
 * false while the V3 shell may still show the frosted header overlay. Force a
 * CLOSE post when no backdrop is present so parent state is reconciled.
 */
export function ensureParentModalOverlayInSyncOnBoot(): void {
  if (!isEmbeddedMode()) return;
  const active = hasActiveModalBackdrop();
  if (!active) {
    shellModalOverlayActive = true;
    setParentModalOverlayActive(false);
    return;
  }
  setParentModalOverlayActive(true);
}

/** Call before hard navigation while a modal may still be open in the DOM. */
export function forceNotifyParentModalOverlayClosed(): void {
  if (!isEmbeddedMode()) return;
  shellModalOverlayActive = true;
  setParentModalOverlayActive(false);
}

/**
 * Shell YouTube-style top progress bar — ask V3 to start/complete its
 * TopProgressBar while this iframe shows CursorProgressIndicator.
 *
 * V3 must listen for:
 *   { type: 'V2_PROGRESS_START' } → startTopProgress()
 *   { type: 'V2_PROGRESS_DONE' }  → doneTopProgress()
 *
 * Ref-counted so overlapping cursor instances do not flicker the shell bar.
 */
let parentProgressActiveCount = 0;

function postParentProgress(type: 'V2_PROGRESS_START' | 'V2_PROGRESS_DONE'): void {
  if (!isEmbeddedMode()) return;
  const parentOrigin = getParentOrigin() ?? FALLBACK_PARENT_ORIGIN;
  if (!parentOrigin) return;

  window.parent.postMessage({ type }, parentOrigin);
}

export function notifyParentProgressStart(): void {
  parentProgressActiveCount += 1;
  if (parentProgressActiveCount === 1) {
    postParentProgress('V2_PROGRESS_START');
  }
}

export function notifyParentProgressDone(): void {
  if (parentProgressActiveCount === 0) return;
  parentProgressActiveCount -= 1;
  if (parentProgressActiveCount === 0) {
    postParentProgress('V2_PROGRESS_DONE');
  }
}

/** Test-only: reset progress ref-count (does not post). */
export function resetParentProgressForTests(): void {
  parentProgressActiveCount = 0;
}

/**
 * Same-origin V2 app URL → V3 shell URL when embedded.
 * Returns null for external / popup / non-app URLs (leave unchanged).
 */
export function toV3ShellHref(href: string): string | null {
  const shellOrigin = FALLBACK_PARENT_ORIGIN?.replace(/\/$/, '');
  if (!shellOrigin || !isEmbeddedMode() || !href) return null;
  if (
    href.startsWith('#') ||
    href.startsWith('mailto:') ||
    href.startsWith('javascript:') ||
    href.startsWith('data:') ||
    href.startsWith('blob:') ||
    href === 'about:blank'
  ) {
    return null;
  }
  try {
    const url = new URL(href, globalThis.location.origin);
    if (url.origin !== globalThis.location.origin) return null;
    if (isV2OnlyTopLevelPath(url.pathname)) return null;
    return `${shellOrigin}${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

/**
 * Origin recipients should open for share/copy links.
 * Embedded: live parent (address-bar / ancestor / referrer), then env, then this frame.
 * Preferring the live parent keeps local :4445 correct even if env points elsewhere.
 * Standalone: this frame's origin — referrer is ignored so other flows stay put.
 */
function getShareableOrigin(): string {
  const currentOrigin = globalThis.location.origin;
  const ancestorOrigin = getAncestorOrigin();
  if (!ancestorOrigin && !isEmbeddedMode()) return currentOrigin;

  return (
    ancestorOrigin ??
    getParentOrigin()?.replace(/\/$/, '') ??
    FALLBACK_PARENT_ORIGIN?.replace(/\/$/, '') ??
    currentOrigin
  );
}

/**
 * Public URL for share/copy links. When this app is embedded, rewrite the
 * child origin to the V3 shell so recipients open the parent app. Standalone
 * V2 keeps `location.origin` unchanged.
 */
export function toShareableAppHref(href: string): string {
  if (!href) return href;
  try {
    const url = new URL(href, globalThis.location.origin);
    if (url.origin !== globalThis.location.origin) return href;
    const publicOrigin = getShareableOrigin();
    if (publicOrigin === url.origin) return href;
    return `${publicOrigin}${url.pathname}${url.search}${url.hash}`;
  } catch {
    return href;
  }
}

/** Open a URL in a new tab; rewrites same-origin app paths to V3 when embedded. */
export function openAppWindow(
  url?: string | URL,
  target = '_blank',
  features = 'noopener,noreferrer'
): Window | null {
  const href = url == null ? '' : String(url);
  return nativeOpen(toV3ShellHref(href) ?? url, target, features);
}

/**
 * Patch `window.open` so every call site (existing `window.open(...)`) opens
 * same-origin app paths in the V3 shell when embedded. Sized popups stay on V2.
 */
export function installV3ShellWindowOpen(): void {
  if (!isEmbeddedMode()) return;

  globalThis.open = (
    url?: string | URL,
    target?: string,
    features?: string
  ): Window | null => {
    if (features && /(?:^|,)\s*(?:width|height)\s*=/i.test(features)) {
      return nativeOpen(url, target, features);
    }
    const href = url == null ? '' : String(url);
    return nativeOpen(toV3ShellHref(href) ?? url, target, features);
  };
}
